/**
 * 業務フロー・ステージの図の URL から、id と案件番号を読む。
 *
 * id は number である（決定 2）。パスパラメータは文字列で来るので、読取へ渡す前にここで関門を
 * 通す。関門を「1 以上の整数」に置くのは契約（`GetFlowInputSchema` の `flowId` / `stageId`）と
 * 同じ線である。
 *
 * ここで弾かないと、読取が入力の検証で失敗した姿が「取得に失敗しました」として出て、URL の
 * 打ち間違いと本当の失敗が利用者に区別できなくなる。ルートの受け口（`receivers/pj-number.ts`）に
 * 同じ形の関数があるが、モジュールを分けてある — あちらは配線のファイルで、画面から参照すると
 * 配線を組み替えるたびに画面が巻き込まれる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー・ステージの図の URL から、契約と同じ関門（1 以上の整数）で id と案件番号を読む。
 * @graph-connects none
 */

/**
 * URL のパスから id（業務フロー・ステージ・案件番号）を読む。1 以上の整数でなければ `null` を返す。
 *
 * `Number()` は空文字を 0 に、前後の空白を無視して数に変えるので、綴りをそのまま確かめる。
 *
 * @param raw パスパラメータの綴り
 * @returns id。1 以上の整数でなければ null
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business パスパラメータの綴りを、契約と同じ関門（1 以上の整数）で id へ読む。
 * @graph-connects none
 */
export function readFlowIdParam(raw: string): number | null {
  if (!/^\d+$/.test(raw)) return null;
  const parsed = Number(raw);
  return parsed >= 1 ? parsed : null;
}

/**
 * 検索条件の案件（`?pj=`）を番号へ読む。`AIRCLOSET-` 付きの綴りも受ける。読めなければ `null`。
 *
 * 検索条件の案件は、数字だけなら数、それ以外は文字列で届く（`parsePjSearch`）。画面の表示は
 * `AIRCLOSET-` を前に付けるので、そのまま貼られた URL も同じ案件として読む。
 *
 * @param raw 検索条件の案件の綴り。未指定なら undefined
 * @returns 案件番号。無い・読めなければ null
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 検索条件の案件の綴り（数字だけか AIRCLOSET- 付き）を、契約と同じ関門で案件番号へ読む。
 * @graph-connects readFlowIdParam [calls] 1 以上の整数としての読み取り
 */
export function readPjSearchParam(raw: number | string | undefined): number | null {
  if (raw === undefined) return null;
  const digits = /^(?:AIRCLOSET-)?(\d+)$/.exec(String(raw).trim())?.[1];
  return digits === undefined ? null : readFlowIdParam(digits);
}
